// Fragment "billing (lot E)" — rempli par son lot de retrofit ; fusionné dans ./index.js.
// Convention : clés plates pointées, valeurs dans les 3 langues, jamais vides.
//
// Contenu W5 : état d'impayé (dunning) + libellés de statut d'abonnement.
// Pourquoi ces clés existent : un prélèvement échoué basculait le cabinet en "suspended"
// immédiatement (site public éteint, console en lecture seule) sans un mot nulle part. Le
// statut `past_due` introduit par W5 laisse la pratique EN LIGNE pendant une fenêtre de
// grâce — mais un état de grâce qui ne se voit pas ne sert à rien : ces libellés sont la
// moitié visible du mécanisme, et `{days}` (jours restants, calculé par le backend qui
// seul détient la fenêtre et l'horloge) est ce qui rend l'avertissement actionnable
// plutôt que décoratif.
// Added by BUG-041 (`acct.*`): the dunning banner above lives inside BillingAdmin.jsx, so
// only a practitioner who had ALREADY worked out that billing was the problem ever saw it.
// A suspended practice still serves reads, so the rest of the console looked perfectly
// normal while every write came back 403 TENANT_SUSPENDED. These three strings are the
// layout-level bar (admin/AdminLayout.jsx) that states the cause on every page and links to
// /admin/billing — the one route that stays writable while suspended.
export default {
  fr: {

    "bill.dunning.title": "Ton dernier paiement n'est pas passé",
    "bill.dunning.body":
      "Ton site et ta console restent en ligne. Sans règlement d'ici {days} jour(s), le cabinet sera suspendu : site public éteint et console en lecture seule.",
    "bill.dunning.lastDay":
      "Ton site et ta console restent en ligne, mais la suspension est imminente. Mets à jour ton moyen de paiement dès maintenant.",
    "bill.dunning.attempts": "Tentatives de prélèvement : {n}",
    "bill.dunning.cta": "Mettre à jour mon moyen de paiement",
    "bill.dunning.suspendedTitle": "Cabinet suspendu pour impayé",
    "bill.dunning.suspendedBody":
      "Ton site public est hors ligne et ta console est en lecture seule. Règle ton abonnement pour tout rétablir immédiatement.",
    "bill.status.active": "Actif",
    "bill.status.pending": "En attente",
    "bill.status.past_due": "Paiement en retard",
    "bill.status.suspended": "Suspendu",
    "bill.status.cancelled": "Résilié",
    "acct.suspended.text":
      "Ton cabinet est suspendu pour impayé — aucune modification ne peut être enregistrée tant que ta facturation n'est pas réglée.",
    "acct.pastDue.text":
      "Ton dernier paiement n'est pas passé — règle-le maintenant pour éviter la suspension de ton cabinet.",
    "acct.billingCta": "Régler ma facturation",
    "acct.trial.text":
      "Essai gratuit — {days} jour(s) restant(s). Ta carte sera débitée automatiquement à la fin de l'essai.",
    "acct.trial.lastDay": "Essai gratuit — dernier jour. Ta carte sera débitée aujourd'hui.",
    "acct.trial.cta": "Voir la facturation",
    "bill.trial.title": "Essai gratuit",
    "bill.trial.daysLeft": "{days} jour(s) restant(s)",
    "bill.trial.lastDay": "Dernier jour de ton essai",
    "bill.trial.endsOn": "L'essai se termine le {date}.",
    "bill.trial.firstCharge": "Ta carte enregistrée sera débitée le {date}.",
    "bill.trial.manageCard": "Gérer le moyen de paiement",
    "bill.trial.convertedTitle": "Essai converti",
    "bill.trial.convertedOn": "Ton abonnement a démarré le {date}.",
    "bill.trial.endedOn": "Ton essai s'est terminé le {date}.",
    "bill.trial.endedEarly": "Ton essai s'est terminé plus tôt que prévu (il devait se terminer le {date}).",
    "bill.trial.chip": "Essai",
    // R5-B2 — a billing call Stripe failed. One sentence per `code` of
    // backend/stripe_refusal.py, mapped by lib/billingErrors.js; `{ref}` is the support
    // reference the refusal carries (the Stripe request id).
    "bill.err.planChangeRefused":
      "Ton forfait n'a pas été modifié et rien n'a été facturé. Contacte le support en indiquant la référence {ref}.",
    "bill.err.planChangeOutcomeUnknown":
      "Nous n'avons pas pu confirmer ton changement de forfait. Ton forfait affiché ici est inchangé : attends quelques minutes avant de réessayer, et contacte le support avec la référence {ref} si le problème persiste.",
    "bill.err.portalUnavailable":
      "Le portail de facturation n'a pas pu s'ouvrir. Réessaie plus tard, ou contacte le support avec la référence {ref}.",
    "bill.err.checkoutRefused":
      "La page de paiement n'a pas pu s'ouvrir et rien n'a été facturé. Contacte le support en indiquant la référence {ref}.",
    "bill.err.checkoutOutcomeUnknown":
      "Nous n'avons pas pu joindre notre prestataire de paiement pour ouvrir la page de paiement. Rien n'a été facturé : réessaie un peu plus tard, et contacte le support avec la référence {ref} si le problème persiste.",
    // A newer billing change (an operator's, or another tab's) took the change lock over
    // before this one was recorded: the page re-reads what is now in force.
    "bill.err.planChangeSuperseded":
      "Une autre modification de ta facturation a pris le relais pendant celle-ci, qui n'a donc pas été enregistrée. La page affiche maintenant ton forfait actuel : contacte le support s'il ne correspond pas à ce que tu attends.",
  },
  en: {

    "bill.dunning.title": "Your last payment didn't go through",
    "bill.dunning.body":
      "Your site and console are still live. If it isn't settled within {days} day(s), the practice will be suspended: public site down and console read-only.",
    "bill.dunning.lastDay":
      "Your site and console are still live, but suspension is imminent. Update your payment method now.",
    "bill.dunning.attempts": "Charge attempts: {n}",
    "bill.dunning.cta": "Update my payment method",
    "bill.dunning.suspendedTitle": "Practice suspended for non-payment",
    "bill.dunning.suspendedBody":
      "Your public site is offline and your console is read-only. Settle your subscription to restore everything immediately.",
    "bill.status.active": "Active",
    "bill.status.pending": "Pending",
    "bill.status.past_due": "Payment overdue",
    "bill.status.suspended": "Suspended",
    "bill.status.cancelled": "Cancelled",
    "acct.suspended.text":
      "Your practice is suspended for non-payment — nothing can be saved until your billing is settled.",
    "acct.pastDue.text":
      "Your last payment didn't go through — settle it now to avoid your practice being suspended.",
    "acct.billingCta": "Settle my billing",
    "acct.trial.text":
      "Free trial — {days} day(s) left. Your card is charged automatically when it ends.",
    "acct.trial.lastDay": "Free trial — last day. Your card will be charged today.",
    "acct.trial.cta": "View billing",
    "bill.trial.title": "Free trial",
    "bill.trial.daysLeft": "{days} day(s) left",
    "bill.trial.lastDay": "Last day of your trial",
    "bill.trial.endsOn": "Trial ends on {date}.",
    "bill.trial.firstCharge": "Your card on file is charged on {date}.",
    "bill.trial.manageCard": "Manage payment method",
    "bill.trial.convertedTitle": "Trial converted",
    "bill.trial.convertedOn": "Your subscription started on {date}.",
    "bill.trial.endedOn": "Your trial ended on {date}.",
    "bill.trial.endedEarly": "Your trial ended early (it was scheduled to end on {date}).",
    "bill.trial.chip": "Trial",
    // R5-B2 — see the fr block.
    "bill.err.planChangeRefused":
      "Your plan was not changed and nothing was charged. Please contact support and quote reference {ref}.",
    "bill.err.planChangeOutcomeUnknown":
      "We could not confirm your plan change. Your plan shown here is unchanged: wait a few minutes before trying again, and contact support with reference {ref} if the problem persists.",
    "bill.err.portalUnavailable":
      "The billing portal could not be opened. Please try again later, or contact support with reference {ref}.",
    "bill.err.checkoutRefused":
      "The payment page could not be opened and nothing was charged. Please contact support and quote reference {ref}.",
    "bill.err.checkoutOutcomeUnknown":
      "We could not reach our payment provider to open the payment page. Nothing was charged: try again a little later, and contact support with reference {ref} if the problem persists.",
    "bill.err.planChangeSuperseded":
      "Another change to your billing took over while this one was being applied, so this one was not recorded. The page now shows your current plan: contact support if it is not what you expect.",
  },
  es: {

    "bill.dunning.title": "Tu último pago no se ha procesado",
    "bill.dunning.body":
      "Tu sitio y tu consola siguen activos. Si no se regulariza en {days} día(s), la consulta quedará suspendida: sitio público apagado y consola en solo lectura.",
    "bill.dunning.lastDay":
      "Tu sitio y tu consola siguen activos, pero la suspensión es inminente. Actualiza tu método de pago ahora.",
    "bill.dunning.attempts": "Intentos de cobro: {n}",
    "bill.dunning.cta": "Actualizar mi método de pago",
    "bill.dunning.suspendedTitle": "Consulta suspendida por impago",
    "bill.dunning.suspendedBody":
      "Tu sitio público está fuera de línea y tu consola en solo lectura. Regulariza tu suscripción para restablecerlo todo de inmediato.",
    "bill.status.active": "Activa",
    "bill.status.pending": "Pendiente",
    "bill.status.past_due": "Pago atrasado",
    "bill.status.suspended": "Suspendida",
    "bill.status.cancelled": "Cancelada",
    "acct.suspended.text":
      "Tu consulta está suspendida por impago: no se puede guardar ningún cambio hasta que regularices tu facturación.",
    "acct.pastDue.text":
      "Tu último pago no se ha procesado: regularízalo ahora para evitar la suspensión de tu consulta.",
    "acct.billingCta": "Regularizar mi facturación",
    "acct.trial.text":
      "Prueba gratuita — quedan {days} día(s). Se cobrará automáticamente a tu tarjeta cuando termine.",
    "acct.trial.lastDay": "Prueba gratuita — último día. Se cobrará hoy a tu tarjeta.",
    "acct.trial.cta": "Ver facturación",
    "bill.trial.title": "Prueba gratuita",
    "bill.trial.daysLeft": "Quedan {days} día(s)",
    "bill.trial.lastDay": "Último día de tu prueba",
    "bill.trial.endsOn": "La prueba termina el {date}.",
    "bill.trial.firstCharge": "Se cobrará a tu tarjeta registrada el {date}.",
    "bill.trial.manageCard": "Gestionar método de pago",
    "bill.trial.convertedTitle": "Prueba convertida",
    "bill.trial.convertedOn": "Tu suscripción comenzó el {date}.",
    "bill.trial.endedOn": "Tu prueba terminó el {date}.",
    "bill.trial.endedEarly": "Tu prueba terminó antes de lo previsto (debía terminar el {date}).",
    "bill.trial.chip": "Prueba",
    // R5-B2 — see the fr block.
    "bill.err.planChangeRefused":
      "Tu plan no se ha modificado y no se ha cobrado nada. Contacta con soporte indicando la referencia {ref}.",
    "bill.err.planChangeOutcomeUnknown":
      "No hemos podido confirmar tu cambio de plan. El plan que ves aquí no ha cambiado: espera unos minutos antes de volver a intentarlo y contacta con soporte con la referencia {ref} si el problema persiste.",
    "bill.err.portalUnavailable":
      "No se ha podido abrir el portal de facturación. Inténtalo más tarde o contacta con soporte con la referencia {ref}.",
    "bill.err.checkoutRefused":
      "No se ha podido abrir la página de pago y no se ha cobrado nada. Contacta con soporte indicando la referencia {ref}.",
    "bill.err.checkoutOutcomeUnknown":
      "No hemos podido contactar con nuestro proveedor de pagos para abrir la página de pago. No se ha cobrado nada: inténtalo un poco más tarde y contacta con soporte con la referencia {ref} si el problema persiste.",
    "bill.err.planChangeSuperseded":
      "Otro cambio en tu facturación se impuso mientras se aplicaba este, así que este no se ha registrado. La página muestra ahora tu plan actual: contacta con soporte si no es el que esperas.",
  },
};
